






















































































































import Vue from 'vue';
import LoadingErrorable from '@/models/util/LoadingErrorable';
import Loader from '@/components/util/Loader.vue';
import SpriteIcon from '@/components/util/SpriteIcon.vue';
import CashShopProvider, { CASH_SHOP_PAGE_SIZE } from '@/api/CashShopProvider';
import ItemsProvider, { CashFilter } from '@/api/ItemsProvider';
import JobProvider, { hiddenClasses } from '@/api/JobProvider';
import IJob from '@/models/jobs/IJob';
import ICashShopEntry, { ICashShopSubTab, ICashShopTab } from '@/models/items/ICashShop';
import { categoryToTabNum, entryMatchesBaseJob, resolveBaseJob } from '@/util/cashShop';
import { hasGrant } from '@/util/Utils';
import CashShopEntryCard from './CashShopEntryCard.vue';
import CashShopEntryDetail from './CashShopEntryDetail.vue';
import DvSkeleton from '@/components/util/DvSkeleton.vue';
import DvEmpty from '@/components/util/DvEmpty.vue';
import DvError from '@/components/util/DvError.vue';
import DvStagger from '@/components/util/DvStagger.vue';
import DvFade from '@/components/util/DvFade.vue';

interface IData {
    tabsData: LoadingErrorable<ICashShopTab[], any>;
    entries: ICashShopEntry[];
    entriesTotal: number;
    entriesLoading: boolean;
    loadingMore: boolean;
    entriesError: string | null;
    loadSeq: number;
    loadedOffset: number;
    searchShown: number;
    sentinelVisible: boolean;
    observer: IntersectionObserver | null;
    activeTab: number;
    activeSubTab: number;
    includeOffSale: boolean;
    searchText: string;
    searchData: LoadingErrorable<ICashShopEntry[], any> | null;
    classFilter: number;
    baseJobs: IJob[];
    parentByJob: { [id: number]: number };
    needJobByItem: { [id: number]: number[] };
}

export default Vue.extend({
    components: {
        Loader,
        SpriteIcon,
        CashShopEntryCard,
        CashShopEntryDetail,
        DvSkeleton,
        DvEmpty,
        DvError,
        DvStagger,
        DvFade,
    },
    data(): IData {
        return {
            tabsData: new LoadingErrorable(),
            entries: [],
            entriesTotal: 0,
            entriesLoading: false,
            loadingMore: false,
            entriesError: null,
            loadSeq: 0,
            loadedOffset: 0,
            searchShown: CASH_SHOP_PAGE_SIZE,
            sentinelVisible: false,
            observer: null,
            activeTab: -1,
            activeSubTab: -1,
            includeOffSale: false,
            searchText: '',
            searchData: null,
            classFilter: 0,
            baseJobs: [],
            parentByJob: {},
            needJobByItem: {},
        };
    },
    computed: {
        techMode(): boolean {
            return this.$store.state.techMode;
        },
        searchActive(): boolean {
            return this.searchData !== null;
        },
        visibleTabs(): ICashShopTab[] {
            if (!this.tabsData.value) {
                return [];
            }

            return this.tabsData.value.filter((t) => t.visible || this.techMode);
        },
        currentTab(): ICashShopTab | null {
            return (this.tabsData.value || []).find((t) => t.mtNum === this.activeTab) || null;
        },
        visibleSubTabs(): ICashShopSubTab[] {
            if (!this.currentTab) {
                return [];
            }

            return this.currentTab.subTabs.filter((s) => s.visible || this.techMode);
        },
        detailSn(): number | null {
            const sn = Number(this.$route.params.sn);
            return sn > 0 ? sn : null;
        },
        classFilterReady(): boolean {
            if (!this.classFilter) {
                return true;
            }

            const src = this.searchActive
                ? (this.searchData && this.searchData.value ? this.searchData.value : [])
                : this.entries;
            return src.every((e) => !e.itemIds.length || this.needJobByItem[e.itemIds[0]] !== undefined);
        },
        displayedEntries(): ICashShopEntry[] {
            return this.applyClassFilter(this.entries);
        },
        displayedSearchEntries(): ICashShopEntry[] {
            return this.applyClassFilter(this.searchData && this.searchData.value ? this.searchData.value : []).slice(0, this.searchShown);
        },
        hasMore(): boolean {
            if (this.searchActive) {
                const all = this.applyClassFilter(this.searchData && this.searchData.value ? this.searchData.value : []);
                return this.searchShown < all.length;
            }

            //  loadedOffset counts raw fetched rows (pre-dedupe) so pagination
            //  never re-requests a window it already consumed.
            return this.loadedOffset < this.entriesTotal;
        },
        countLabel(): string {
            if (this.searchActive || !this.entriesTotal) {
                return '';
            }

            if (this.classFilter) {
                return this.classFilterReady ? `${this.displayedEntries.length} shown of ${this.entries.length} loaded` : '';
            }

            return `${this.entriesTotal.toLocaleString()} item${this.entriesTotal === 1 ? '' : 's'}`;
        },
    },
    created() {
        this.loadTabs();
        this.loadJobs();
    },
    mounted() {
        this.observer = new IntersectionObserver((recs) => {
            this.sentinelVisible = recs.some((r) => r.isIntersecting);
            if (this.sentinelVisible) {
                this.loadMore();
            }
        }, { rootMargin: '600px 0px' });
        this.observer.observe(this.$refs.sentinel as Element);
    },
    beforeDestroy() {
        if (this.observer) {
            this.observer.disconnect();
        }
    },
    watch: {
        includeOffSale() {
            this.loadEntries();
        },
        searchActive() {
            this.searchShown = CASH_SHOP_PAGE_SIZE;
        },
        //  A whole load can resolve while the "Filtering by class" loader is up;
        //  once filtering flips ready, keep loading if the sentinel is on screen.
        classFilterReady(ready: boolean) {
            if (ready) {
                this.$nextTick(() => this.maybeChainLoad());
            }
        },
        techMode() {
            //  Falling out of tech mode can leave a hidden tab/sub-tab active.
            if (!this.techMode) {
                this.includeOffSale = false;
                const tab = this.currentTab;
                if (!tab || !tab.visible) {
                    this.selectFirstVisible();
                } else {
                    const sub = tab.subTabs.find((s) => s.stNum === this.activeSubTab);
                    if (!sub || !sub.visible) {
                        this.selectTab(tab.mtNum);
                    }
                }
            }
        },
        '$store.state.regionCode'() {
            this.clearSearch();
            this.classFilter = 0;
            this.needJobByItem = {};
            this.loadJobs();
            this.loadTabs();
        },
    },
    methods: {
        async loadTabs() {
            const loader = new LoadingErrorable<ICashShopTab[], any>();
            this.tabsData = loader;
            this.loadSeq++;
            this.entries = [];
            this.entriesTotal = 0;
            this.loadedOffset = 0;
            this.entriesError = null;
            loader.startLoad();
            try {
                const tabs = await CashShopProvider.getTabs();
                if (this.tabsData !== loader) return;
                loader.done(tabs);
                await this.activateForDeepLink(tabs);
            } catch (error) {
                if (this.tabsData !== loader) return;
                loader.failed(error);
            }
        },
        async activateForDeepLink(tabs: ICashShopTab[]) {
            const sn = this.detailSn;
            if (sn !== null) {
                try {
                    const entry = await CashShopProvider.getEntry(sn);
                    if (entry) {
                        const tabNum = categoryToTabNum(entry.category, entry.subCategory, tabs);
                        const tab = tabNum !== null ? tabs.find((t) => t.mtNum === tabNum) : null;
                        if (tab && tab.subTabs.some((s) => s.stNum === entry.subCategory)) {
                            this.activeTab = tab.mtNum;
                            this.activeSubTab = entry.subCategory;
                            this.loadEntries();
                            return;
                        }
                    }
                } catch (e) {
                    //  Fall through to the default tab; the panel shows its own error.
                }
            }

            this.selectFirstVisible();
        },
        selectFirstVisible() {
            const first = this.visibleTabs[0];
            if (first) {
                this.selectTab(first.mtNum);
            }
        },
        selectTab(mtNum: number) {
            this.activeTab = mtNum;
            const tab = this.currentTab;
            const firstSub = tab ? tab.subTabs.filter((s) => s.visible || this.techMode)[0] : null;
            this.activeSubTab = firstSub ? firstSub.stNum : -1;
            this.loadEntries();
        },
        selectSubTab(stNum: number) {
            this.activeSubTab = stNum;
            this.loadEntries();
        },
        async loadEntries() {
            const seq = ++this.loadSeq;
            this.entries = [];
            this.entriesTotal = 0;
            this.loadedOffset = 0;
            this.entriesError = null;
            if (this.activeTab < 0 || this.activeSubTab < 0) {
                this.entriesLoading = false;
                return;
            }

            this.entriesLoading = true;
            try {
                const page = await CashShopProvider.getEntriesPage(this.activeTab, this.activeSubTab, this.includeOffSale, 0);
                if (seq !== this.loadSeq) return;
                //  Guard against duplicate SNs within a single page (composite
                //  tables can repeat ids; duplicate v-for keys break the grid).
                const seen = new Set<number>();
                this.entries = page.entries.filter((e) => {
                    if (seen.has(e.sn)) {
                        return false;
                    }

                    seen.add(e.sn);
                    return true;
                });
                this.entriesTotal = page.total;
                this.loadedOffset = page.entries.length;
                this.ensureJobData();
                this.$nextTick(() => this.maybeChainLoad());
            } catch (error) {
                if (seq !== this.loadSeq) return;
                this.entriesError = String(error);
            } finally {
                if (seq === this.loadSeq) {
                    this.entriesLoading = false;
                }
            }
        },
        async loadMore() {
            if (this.loadingMore || this.entriesLoading) {
                return;
            }

            if (this.searchActive) {
                this.searchShown += CASH_SHOP_PAGE_SIZE;
                return;
            }

            if (!this.hasMore) {
                return;
            }

            const seq = this.loadSeq;
            this.loadingMore = true;
            try {
                const page = await CashShopProvider.getEntriesPage(this.activeTab, this.activeSubTab, this.includeOffSale, this.loadedOffset);
                if (seq !== this.loadSeq) return;
                //  Guard against duplicate SNs both across page boundaries and
                //  within the incoming page itself (composite tables can repeat
                //  ids; duplicate v-for keys break the grid).
                const have = new Set(this.entries.map((e) => e.sn));
                const fresh = page.entries.filter((e) => {
                    if (have.has(e.sn)) {
                        return false;
                    }

                    have.add(e.sn);
                    return true;
                });
                this.entries = this.entries.concat(fresh);
                this.entriesTotal = page.total;
                this.loadedOffset += page.entries.length;
                if (!page.entries.length) {
                    //  Defensive: an empty page with hasMore still true would
                    //  otherwise chain-load forever.
                    this.entriesTotal = this.loadedOffset;
                }
                this.ensureJobData();
                this.$nextTick(() => this.maybeChainLoad());
            } catch (error) {
                if (seq === this.loadSeq) {
                    this.entriesError = String(error);
                }
            } finally {
                this.loadingMore = false;
            }
        },
        //  With an aggressive class filter a whole appended page can be filtered
        //  out, so no DOM growth re-triggers the observer — chain manually while
        //  the sentinel is still on screen. Measure the rect fresh instead of
        //  trusting sentinelVisible: cache-served pages append faster than the
        //  observer reports the sentinel leaving the viewport, and the stale
        //  flag would chain-load the entire list.
        sentinelInView(): boolean {
            const el = this.$refs.sentinel as Element | undefined;
            if (!el) {
                return false;
            }

            return el.getBoundingClientRect().top < window.innerHeight + 600;
        },
        maybeChainLoad() {
            if (this.sentinelInView() && this.hasMore && !this.loadingMore && !this.entriesLoading) {
                this.loadMore();
            }
        },
        async loadJobs() {
            try {
                let all = await JobProvider.getAllJobs();
                if (!hasGrant('ref.pdev.duck')) {
                    all = all.filter((j) => hiddenClasses.indexOf(j.id) === -1);
                }

                this.baseJobs = all.filter((j) => j.jobNumber === 0 && j.id > 0);
                const map: { [id: number]: number } = {};
                for (const j of all) {
                    map[j.id] = j.parentJobId;
                }
                this.parentByJob = map;
            } catch (e) {
                //  No job data -> the filter row simply doesn't render.
                this.baseJobs = [];
            }
        },
        setClassFilter(jobId: number) {
            this.classFilter = this.classFilter === jobId ? 0 : jobId;
            this.ensureJobData();
        },
        //  Distinct base classes the entry's primary item is restricted to
        //  ([] = usable by everyone or data not loaded yet), capped for display.
        restrictedJobsFor(e: ICashShopEntry): IJob[] {
            const need = e.itemIds.length ? this.needJobByItem[e.itemIds[0]] : undefined;
            if (!need || !need.length) {
                return [];
            }

            const baseIds = [...new Set(need.map((id) => resolveBaseJob(id, this.parentByJob)))];
            return baseIds
                .map((id) => this.baseJobs.find((j) => j.id === id))
                .filter((j): j is IJob => !!j)
                .slice(0, 3);
        },
        applyClassFilter(entries: ICashShopEntry[]): ICashShopEntry[] {
            if (!this.classFilter || !this.classFilterReady) {
                return entries;
            }

            return entries.filter((e) => {
                const need = e.itemIds.length ? this.needJobByItem[e.itemIds[0]] : [];
                return entryMatchesBaseJob(need || [], this.classFilter, this.parentByJob);
            });
        },
        //  Fetched for every loaded page (not just when filtering): the cards
        //  show class-restriction icons from this data.
        async ensureJobData() {
            const src = [
                ...this.entries,
                ...(this.searchData && this.searchData.value ? this.searchData.value : []),
            ];
            const ids = [...new Set(src
                .map((e) => e.itemIds[0])
                .filter((id) => id && this.needJobByItem[id] === undefined))];
            if (!ids.length) {
                return;
            }

            try {
                const fetched = await CashShopProvider.getNeedJobClasses(ids);
                this.needJobByItem = { ...this.needJobByItem, ...fetched };
            } catch (e) {
                //  Fail open: treat unfetchable items as usable-by-all so the
                //  grid never wedges on the filtering loader.
                const fallback: { [id: number]: number[] } = {};
                for (const id of ids) {
                    fallback[id] = [];
                }
                this.needJobByItem = { ...this.needJobByItem, ...fallback };
            }
        },
        openEntry(sn: number) {
            if (String(this.$route.params.sn) !== String(sn)) {
                this.$router.push(`/items/cashshop/${sn}`);
            }
        },
        closeDetail() {
            this.$router.push('/items/cashshop');
        },
        async doSearch() {
            const name = this.searchText.trim();
            if (!name) {
                return;
            }

            const loader = new LoadingErrorable<ICashShopEntry[], any>();
            this.searchData = loader;
            loader.startLoad();
            try {
                const result = await ItemsProvider.search({
                    name,
                    ranks: [],
                    types: [],
                    parts: [],
                    cash: CashFilter.ALL,
                    categories: [],
                    classes: [],
                    minLevel: 0,
                    maxLevel: 999,
                });
                const itemIds = result.items.map((i) => i.id).slice(0, 200);
                let entries = await CashShopProvider.findByItemIds(itemIds);
                if (!this.includeOffSale) {
                    entries = entries.filter((e) => e.onSale);
                }
                loader.done(entries);
                this.searchShown = CASH_SHOP_PAGE_SIZE;
                this.ensureJobData();
            } catch (error) {
                loader.failed(error);
            }
        },
        clearSearch() {
            this.searchText = '';
            this.searchData = null;
        },
        originLabel(e: ICashShopEntry): string {
            const tabs = this.tabsData.value || [];
            const tabNum = categoryToTabNum(e.category, e.subCategory, tabs);
            const tab = tabNum !== null ? tabs.find((t) => t.mtNum === tabNum) : null;
            if (!tab) {
                return '';
            }

            const sub = tab.subTabs.find((s) => s.stNum === e.subCategory);
            return sub ? `${tab.name} › ${sub.name}` : tab.name;
        },
    },
});
